'use strict';

import PropTypes from 'prop-types';
import { Component } from 'react';
import Modal from 'react-modal';
import { Link } from 'react-router';

import Alert from '@components/Widgets/Alert/Alert.react';
import * as Sentry from '@sentry/react';
import allMealTypes from '@tables/meal-types';
import AuthStore from '@stores/AuthStore';
import UserActions from '@actions/UserActions';
import { getConfigurationIssues, getMealSearchParamsForProfile } from '@pro/utils/Patients';
import Analytics from '@utils/Analytics';
import { getConfig } from '@utils/Env';
import ContactFormModal from '../Widgets/ContactFormModal.react';

import './ProfileDoctorModal.scss';

export default class ProfileDoctorModal extends Component {
    static propTypes = {};

    static contextTypes = {
        user: PropTypes.object,
    };

    constructor(props, context) {
        super(props, context);

        this.state = {
            drawer: false,
            culprit: { reason: null },

            preferences: context.user.preferences || {},
        };
    }

    componentDidMount = async () => {
        try {
            this.setState({ drawer: true });
            await this.analyzeProfile();
        } catch (error) {
            this.setState({ error: (error && error.message) || error || 'unknown error' });
        }
    };

    componentDidUpdate(prevProps, prevState) {
        const { culprit } = this.state;
        const { error } = this.props;

        if (prevState.culprit !== culprit) {
            Analytics.planGenerationError({ culprit, error });
        }
    }

    whichMealsAreIncluded = (profile) => {
        const { preferences = {}, family = [] } = profile || {};
        
        // Get meal types from preferences.meal_types where each has main_dish property
        const mealTypes = preferences.meal_types || [];
        
        // Check if each meal type has participants (meaning it's enabled)
        const breakfasts = mealTypes.some(mt => mt.main_dish === 'Breakfast' && mt.participants?.length > 0);
        const lunches = mealTypes.some(mt => mt.main_dish === 'Lunch' && mt.participants?.length > 0);
        const dinners = mealTypes.some(mt => mt.main_dish === 'Dinner' && mt.participants?.length > 0);
        const snacks = mealTypes.some(mt => mt.main_dish === 'Snack' && mt.participants?.length > 0);

        return { breakfasts, lunches, dinners, snacks };
    };

    analyzeProfile = async () => {
        const { preferences } = this.state;
        const { user } = this.context;

        this.setState({ loading: true });

        if (!preferences) {
            return this.setState({ loading: false });
        }

        if ((preferences.limit_tags || []).includes('Basic')) {
            return this.setState({ culprit: { reason: 'Basic' }, loading: false });
        }

        const { breakfasts, lunches, dinners, snacks } = this.whichMealsAreIncluded(user);

        if (snacks && !breakfasts && !lunches && !dinners) {
            return this.setState({ culprit: { reason: 'meals are turned off' }, loading: false });
        }

        if (breakfasts && lunches && dinners && !snacks) {
            return this.setState({ culprit: { reason: 'snacks' }, loading: false });
        }

        const { warnings, errors } = getConfigurationIssues(user);

        // If there are errors, set them immediately and no need to continue.
        if (errors.length) {
            return this.setState({ culprit: { reason: 'profile-error', errors }, loading: false });
        }

        const qualifying = {};

        for (const { threshold, mainDish: mealType, plural } of allMealTypes) {
            // Removed check for deprecated user.preferences[prop] - proceed with all meal types

            const params = getMealSearchParamsForProfile(mealType, user);

            params.size = 0; // we don't need results, we just want to know the total
            
            try {
                const response = await AuthStore.fetch(getConfig('recipe_api') + '/search', {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json; schema=search/advanced/1'},
                    body: JSON.stringify(params),
                });

                qualifying[mealType] = {
                    total: response.total,
                    params,
                };

                if (response.total && response.total < threshold) {
                    warnings.push(`Your profile matches very few ${plural.toLowerCase()}. We may not be able to build a meal plan matching your profile.`);
                } else if (!response.total) {
                    errors.push(`No matching ${plural.toLowerCase()} found. We are unable to build a meal plan matching this profile.`);
                }
            } catch (error) {
                // Capture error details to Sentry for monitoring
                Sentry.captureException(error, {
                    tags: {
                        component: 'ProfileDoctorModal',
                        action: 'meal_search',
                        meal_type: mealType
                    },
                    extra: {
                        mealType,
                        plural,
                        params,
                        userId: user?.id
                    }
                });

                // A thrown fetch is a connection failure, not a too-restrictive profile.
                return this.setState({ culprit: { reason: 'connection' }, loading: false });
            }
        }

        if (warnings.length || errors.length) {
            return this.setState({ culprit: { reason: 'profile-error', errors, warnings }, loading: false });
        }

        this.setState({ culprit: { reason: 'unknown' }, loading: false });
    };

    closeModal = () => {
        this.setState({ drawer: false }, () => setTimeout(this.props.closeModal, 333));
    };

    retryPopulate = () => {
        const { preferences } = this.state;
        const newPrefs = {...preferences};

        if (!preferences.equipment?.includes("Stovetop")) {
            newPrefs.equipment.push("Stovetop");
            UserActions.updateSpecificMeta({preferences: newPrefs}).then(this.closeModal);
        } else {
            this.closeModal();
        }
    };

    toggleBasicOffAndRetry = () => {
        let { preferences } = this.state;

        preferences = preferences || {};
        let { limit_tags = [] } = preferences;

        if (limit_tags.includes('Basic')) {
            limit_tags.splice(limit_tags.indexOf('Basic'), 1);
        } else {
            limit_tags.push('Basic');
        }

        preferences.limit_tags = limit_tags;

        UserActions.updateSpecificMeta({ preferences }).then(this.retryPopulate);
    };

    toggleMealsOnAndRetry = () => {
        const { user } = this.context;
        let { preferences } = this.state;

        preferences = preferences || {};
        preferences.meal_types = preferences.meal_types || [];
        
        // Get all participants (USER + family member UUIDs)
        const allParticipants = ['USER'];
        if (user.family) {
            user.family.forEach(member => {
                allParticipants.push(member.uuid);
            });
        }
        
        // Only update existing meal types, don't add new ones
        const mealTypesToEnable = ['Breakfast', 'Lunch', 'Dinner', 'Snack'];
        
        preferences.meal_types.forEach(mealType => {
            if (mealTypesToEnable.includes(mealType.main_dish)) {
                mealType.participants = [...allParticipants];
            }
        });

        if (preferences.meal_types?.length > 0) {
            preferences.meal_types = preferences.meal_types.map((mealType) => {
                // Add user to participants for all meal types
                const participants = mealType.participants || [];
                if (!participants.includes('USER')) {
                    participants.push('USER');
                }
                return {
                    ...mealType,
                    participants,
                };
            });
        }

        UserActions.updateSpecificMeta({ preferences }).then(this.retryPopulate);
    };

    toggleSnacksOnAndRetry = () => {
        const { user } = this.context;
        let { preferences } = this.state;
        const userUuid = user.uuid || 'USER';

        preferences = preferences || {};
        preferences.meal_types = preferences.meal_types || [];
        
        // Get all participants (USER + family member UUIDs)
        const allParticipants = ['USER'];
        if (user.family) {
            user.family.forEach(member => {
                allParticipants.push(member.uuid);
            });
        }
        
        // Only update existing Snack meal type, don't add new one
        const snackMealType = preferences.meal_types.find(mt => mt.main_dish === 'Snack');
        if (snackMealType) {
            snackMealType.participants = [...allParticipants];
        }

        if (preferences.meal_types?.length > 0) {
            preferences.meal_types = preferences.meal_types.map((mealType) => {
                // Add user to participants for snack meal types
                if (mealType.main_dish === 'Snack') {
                    const participants = mealType.participants || [];
                    if (!participants.includes(userUuid)) {
                        participants.push(userUuid);
                    }
                    return {
                        ...mealType,
                        participants,
                    };
                }
                return mealType;
            });
        }

        UserActions.updateSpecificMeta({ preferences }).then(this.retryPopulate);
    };

    renderModalBody = () => {
        const { culprit, loading } = this.state;
        const { user } = this.context;
        const { retryCount, retry } = this.props;

        const { my_dietitian, practice_type = 'consumer' } = user;
        const { reason = 'unknown' } = culprit;

        if (loading) {
            return (
                <div className="el-modal-container el-modal2-container">
                    <div className="el-modal-loading">
                        <p>Analyzing your profile...</p>
                        <i className="icon-spinner2" />
                    </div>
                </div>
            );
        }

        if (reason == 'meals are turned off') {
            return (
                <div className="el-modal-container el-modal2-container">
                    <div className="el-modal-body-container el-modal2-body-container el-fonts">
                        <p>
                            We’re sorry, we were unable to build a meal plan due to too few meals in a day. Please
                            enable breakfast, lunch and dinner options and try again.
                        </p>
                    </div>

                    <footer>
                        {retryCount > 0 ? (
                            <button className="el-modal-cancel-btn" onClick={this.closeModal}>
                                close
                            </button>
                        ) : null}

                        <button className="el-modal-ok-btn" onClick={this.toggleMealsOnAndRetry}>
                            Enable meals and retry
                        </button>
                    </footer>
                </div>
            );
        }

        if (reason == 'Basic') {
            return (
                <div className="el-modal-container el-modal2-container">
                    <div className="el-modal-body-container el-modal2-body-container el-fonts">
                        <p>We are unable to recommend Simple Recipes that meet your nutrition requirements. </p>
                        <p>
                            <em>
                                The Simple Recipes setting has been turned OFF in order to create your nutritious meal
                                recommendations.
                            </em>
                        </p>
                    </div>

                    <footer>
                        {retryCount > 0 ? (
                            <button className="el-modal-cancel-btn" onClick={this.closeModal}>
                                close
                            </button>
                        ) : null}

                        <button onClick={this.toggleBasicOffAndRetry} className="el-modal-ok-btn">
                            Retry
                        </button>
                    </footer>
                </div>
            );
        }

        if (reason == 'snacks') {
            return (
                <div className="el-modal-container el-modal2-container">
                    <div className="el-modal-body-container el-modal2-body-container el-fonts">
                        {retryCount == 0 ? (
                            <p>
                                We’re sorry, we are unable to find meals that meet your nutrition requirements due to
                                snacks being excluded.
                            </p>
                        ) : (
                            <p>
                                We’re sorry, we are still unable to find meals that meet your nutrition requirements due
                                to snacks being excluded.
                            </p>
                        )}

                        <p>
                            <em>
                                Snacks are an essential component of a day full of meals. We've turned snacks on for
                                you.
                            </em>
                        </p>
                    </div>

                    <footer>
                        {retryCount > 0 ? (
                            <button className="el-modal-cancel-btn" onClick={this.closeModal}>
                                close
                            </button>
                        ) : null}

                        <button className="el-modal-ok-btn" onClick={this.toggleSnacksOnAndRetry}>
                            Retry
                        </button>
                    </footer>
                </div>
            );
        }

        let /* 's do */ getHelp = ( // No, it's embarrasing...
            <div>
                <p>Please try removing unnecessary avoidances, increasing leftovers, or relaxing prep time limits and then retrying.</p>
                <p>If the problem persists, please contact <ContactFormModal defaultSubject="technical" sourceLabel="Doctor Profile Setup - Technical Support">support@eatlove.is</ContactFormModal> for assistance</p>
            </div>
        ) // Not for me it's not! ;)

        if (practice_type === 'dietetics' && my_dietitian && my_dietitian.email !== 'redacted') {
            getHelp = (
                <div>
                    <p>
                        Please try removing unnecessary avoidances, increasing leftovers, or relaxing prep time limits
                        and then retrying.
                    </p>
                    <p>
                        If the problem persists, please contact{' '}
                        <a href={`mailto:${my_dietitian.email}?subject=Unable to find Meals`}>
                            {my_dietitian.name}
                            {my_dietitian.credentials ? ', ' : null} <em>{my_dietitian.credentials}</em>
                        </a>{' '}
                        for assistance.
                    </p>
                </div>
            );
        }

        if (reason === 'connection') {
            return (
                <div className="el-modal-container el-modal2-container">
                    <div className="el-modal-body-container el-modal2-body-container el-fonts">
                        <Alert type="error">
                            We couldn't reach the server to build your meal plan. Please check your internet
                            connection and try again.
                        </Alert>
                    </div>

                    <footer>
                        {retryCount > 0 ? (
                            <button className="el-modal-cancel-btn" onClick={this.closeModal}>
                                close
                            </button>
                        ) : null}

                        <button className="el-modal-ok-btn" onClick={() => retry()}>Retry</button>
                    </footer>
                </div>
            );
        }

        if (reason === 'profile-error') {
            const { errors = [], warnings = [] } = culprit;

            return (
                <div className="el-modal-container el-modal2-container">
                    <div className="el-modal-body-container el-modal2-body-container el-fonts">
                        {errors && errors.length ? <Alert type="error">{errors[0]}</Alert> : null}

                        {warnings && warnings.length > 0 && !(errors && errors.length) ? (
                            <Alert type="warning">{warnings[0]}</Alert>
                        ) : null}

                        {retryCount > 0 ? getHelp : null}
                    </div>

                    <footer>
                        <Link to="/preferences">
                            <button className="el-modal-cancel-btn" onClick={this.closeModal}>
                                edit preferences
                            </button>
                        </Link>

                        <button className="el-modal-ok-btn" onClick={() => retry()}>Retry</button>
                    </footer>
                </div>
            );
        }

        return (
            <div className="el-modal-container el-modal2-container">
                <div className="el-modal-loading">
                    {retryCount == 0 ? (
                        <p>We were not able to build a meal plan that fits your profile.</p>
                    ) : (
                        <p>We were still not able to build a meal plan that fits your profile.</p>
                    )}

                    {retryCount > 0 ? getHelp : null}
                </div>

                <footer>
                    {retryCount > 0 || user.role === 'admin' ? (
                        <button className="el-modal-cancel-btn" onClick={this.closeModal}>
                            close
                        </button>
                    ) : null}

                    <button className="el-modal-ok-btn" onClick={() => retry()}>Retry</button>
                </footer>
            </div>
        );
    };

    render = () => {
        const { drawer } = this.state;
        const { user } = this.context;

        if (!user) {
            return <span />;
        }

        return (
            <Modal
                isOpen={true}
                closeModal={() => false}
                onRequestClose={this.closeModal}
                className="el-modal el-modal2 profile-doctor-modal"
                overlayClassName="el-modal-overlay"
                contentLabel="EatLove coachmark"
                closeTimeoutMS={250}
            >
                {this.renderModalBody()}
            </Modal>
        );
    };
}
